import './styles.scss';

import { useContext } from 'react';

import { featureFlags, restrictNavByPermission } from '../../config/feature-flags';
import Backtesting from '../../pages/backtesting';
import CarryBasisPage from '../../pages/carry-basis';
import Dashboard from '../../pages/dashboard/dashboard';
import ExoticsPricer from '../../pages/exotics-pricer';
import MarketProbabilitiesPage from '../../pages/market-probabilities';
import OptionsFlowPage from '../../pages/options-flow';
import OTCTrader from '../../pages/otc-trader';
import PortfolioManagement from '../../pages/portfolio-management';
import PorfolioPerfomance from '../../pages/portfolio-performance';
import Settings from '../../pages/settings';
import { BacktestingProvider } from '../../providers/backtesting/backtesting-provider';
import { MarketProbabilitiesProvider } from '../../providers/market-probabilities-provider';
import { DecayWidgetProvider } from '../../providers/decay-widget/decay-widget-provider';
import { DemoBlurProvider } from '../../providers/demo-blur';
import AuthContext from '../../providers/auth/auth-service';
import { GlobalContext, GlobalTabsKeysEnum } from '../../providers/global-provider';
import { hasPermissionFor } from '../../providers/permission-mapping';
import { OptionsFlowProvider } from '../../providers/options-flow/options-flow-provider';
import { OTCTraderProvider } from '../../providers/otc-trader/otc-trader-provider';
import { PNLProvider } from '../../providers/pnl-provider';
import { PortfolioManagementProvider } from '../../providers/portfolio-management/portfolio-management-provider';
import { PortfolioPerformanceProvider } from '../../providers/portfolio-performance';
import { PositionsFiltersProvider } from '../../providers/positions/positions-filters-provider';
import { PositionsProvider } from '../../providers/positions/positions-provider';
import { RiskFiltersProvider } from '../../providers/risk-management/risk-filters-provider';
import { RiskManagementProvider } from '../../providers/risk-management/risk-management-provider';
import { StrikeReportProvider } from '../../providers/strike-report';
import { TransactionsFiltersProvider } from '../../providers/transactions/transactions-filters-provider';
import { TransactionsProvider } from '../../providers/transactions/transactions-provider';
import { NetworkConnectionModal } from '../../providers/websocket-context/network-connection-modal';
import ImmersiveAppBar from '../appbar/appbar';
import ImmersiveFooter from '../footer/footer';

export const Layout = () => {
  const {
    currentTab,
    renderSettings,
    showReloadMessage,
    setShowReloadMessage,
    connectionErrorVariant,
  } = useContext(GlobalContext);
  const { permissions } = useContext(AuthContext);

  const tabs: { [key: string]: any } = {
    [GlobalTabsKeysEnum.marketData]: <Dashboard />,
    [GlobalTabsKeysEnum.portfolioManagement]: (
      <PortfolioManagementProvider>
        <RiskFiltersProvider>
          <DecayWidgetProvider>
            <RiskManagementProvider>
              <PNLProvider>
                <PositionsFiltersProvider>
                  <PositionsProvider>
                    <TransactionsFiltersProvider>
                      <TransactionsProvider>
                        <StrikeReportProvider>
                          <PortfolioManagement />
                        </StrikeReportProvider>
                      </TransactionsProvider>
                    </TransactionsFiltersProvider>
                  </PositionsProvider>
                </PositionsFiltersProvider>
              </PNLProvider>
            </RiskManagementProvider>
          </DecayWidgetProvider>
        </RiskFiltersProvider>
      </PortfolioManagementProvider>
    ),
    [GlobalTabsKeysEnum.OTCTrader]: (
      <OTCTraderProvider>
        <OTCTrader />
      </OTCTraderProvider>
    ),
    // Feature-gated (REACT_APP_FEATURE_MARKET_PROBABILITIES): when OFF the
    // provider is never mounted, so useMtdCandles / useMarketProbabilitiesFeed
    // never subscribe — zero `subscribe.market_probabilities` traffic.
    ...(featureFlags.marketProbabilities
      ? {
          [GlobalTabsKeysEnum.marketProbabilities]: (
            <MarketProbabilitiesProvider>
              <MarketProbabilitiesPage />
            </MarketProbabilitiesProvider>
          ),
        }
      : {}),
    // Feature-gated (REACT_APP_FEATURE_OPTIONS_FLOW): when OFF the provider is
    // never mounted, so useOptionsFlowFeed never subscribes — zero flow-feed
    // traffic.
    ...(featureFlags.optionsFlow
      ? {
          [GlobalTabsKeysEnum.optionsFlow]: (
            <OptionsFlowProvider>
              <OptionsFlowPage />
            </OptionsFlowProvider>
          ),
        }
      : {}),
    // Backtesting: mounted + routable, but intentionally NOT in the appbar menu
    // (mirrors staging — the backtester engine only runs on DEMO/PROD_OVH).
    [GlobalTabsKeysEnum.backtesting]: (
      <BacktestingProvider>
        <Backtesting />
      </BacktestingProvider>
    ),
    // Feature-gated (REACT_APP_FEATURE_PORTFOLIO_PERFORMANCE): when OFF neither
    // the provider (credentials fetch on connect) nor the page (reference-strategy
    // list_prices + candlestick polls, via useReferenceStrategyTokens /
    // useReferenceStrategyReports) is mounted — zero of those calls are issued.
    ...(featureFlags.portfolioPerformance
      ? {
          [GlobalTabsKeysEnum.portfolioPerformance]: (
            <PortfolioPerformanceProvider>
              <PorfolioPerfomance />
            </PortfolioPerformanceProvider>
          ),
        }
      : {}),
    // ExoticsPricer wraps itself in ExoticsTabsProvider + per-tab
    // PricingProvider (see pages/exotics-pricer/index.tsx), so no extra
    // provider wrapper is needed here — mirrors dev's layout mount.
    [GlobalTabsKeysEnum.pricing]: <ExoticsPricer />,
    // Carry & Basis: self-contained page, no provider wrapper (mirrors staging).
    // Feature-gated (REACT_APP_FEATURE_CARRY_BASIS): when OFF the page is never
    // mounted, so useImpliedVolSmile / useFuturesSnapshot never poll — zero
    // `implied_volatility_smile` traffic.
    ...(featureFlags.carryBasis ? { [GlobalTabsKeysEnum.carryBasis]: <CarryBasisPage /> } : {}),
  };

  const renderTabs = () => {
    // IM-2594: when the flag is OFF (production default) every key passes, so
    // tabs render exactly as today. When ON, a tab renders only if the user
    // holds its permission (permissionForTab) — composed ON TOP of the
    // feature-flag spreads above (a tab shows iff flag-present AND permitted).
    // This drops the CONTENT of any tab the user is NOT permitted for, even via
    // a direct URL. Note the nav (appbar) gates on each LINK's own permission,
    // which can be stricter than the tab's: a link hidden from the nav may still
    // be reachable by URL when the user holds the tab's permission -- nav
    // restriction is not a content lock.
    return Object.keys(tabs)
      .filter((key) => !restrictNavByPermission || hasPermissionFor(permissions, key))
      .map((key) => {
        const isCurrentTab = currentTab === key;

        return (
          <div key={key} className={`tab-wrapper ${isCurrentTab && 'visible'}`}>
            {tabs[key]}
          </div>
        );
      });
  };

  return (
    <DemoBlurProvider>
      <div className={`layout-container ${renderSettings && 'show-settings'}`}>
        <ImmersiveAppBar />
        {renderTabs()}
        {<div className="render-settings">{renderSettings && <Settings />}</div>}
        <NetworkConnectionModal
          open={showReloadMessage}
          handleClose={() => setShowReloadMessage(false)}
          variant={connectionErrorVariant}
        />
        <ImmersiveFooter />
      </div>
    </DemoBlurProvider>
  );
};
